import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  Avatar,
} from '@mui/material';
import {
  Refresh,
  Visibility,
  Add,
  CheckCircle,
  Cancel,
  Description,
  Person,
  Business,
  AttachMoney,
  CalendarToday,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DiscountService from '../../services/discountService';

const DiscountList = ({ user }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [contractDialog, setContractDialog] = useState(false);
  const [loadingContract, setLoadingContract] = useState(false);

  // Fetch discounts
  const fetchDiscounts = async () => {
    if (!user?.userId) return;

    setLoading(true);
    setError('');

    const result = await DiscountService.getDiscountsBySeller(user.userId);

    if (result.success) {
      setDiscounts(result.data || []);
    } else {
      setError(result.message);
      setDiscounts([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchDiscounts();
  }, [user]);

  // Format currency
  const formatCurrency = (amount, currency = 'EUR') => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const parts = formatter.formatToParts(amount);
    let formatted = '';
    for (const part of parts) {
      if (part.type === 'currency') {
        formatted += part.value + ' ';
      } else {
        formatted += part.value;
      }
    }
    return formatted.trim();
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 1: // Pending
        return 'warning';
      case 2: // Approved
        return 'info';
      case 3: // Completed
        return 'success';
      case 4: // Rejected
        return 'error';
      case 5: // Cancelled
        return 'default';
      default:
        return 'default';
    }
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return 'Pending';
      case 2:
        return 'Approved';
      case 3:
        return 'Completed';
      case 4:
        return 'Rejected';
      case 5:
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  // Handle view details
  const handleViewDetails = (discount) => {
    setSelectedDiscount(discount);
    setDetailDialog(true);
  };

  // Handle cancel discount
  const handleCancel = async (discountId) => {
    if (!window.confirm('Are you sure you want to cancel this discount loan?')) {
      return;
    }

    const result = await DiscountService.cancelDiscount(discountId);
    if (result.success) {
      fetchDiscounts();
    }
  };

  // Generate contract data from discount
  const generateContractData = (discount) => {
    if (!discount) return null;
    
    const today = new Date();
    const dueDate = new Date(discount.dueDate);
    const currency = discount.currency || 'EUR';
    
    return {
      contractId: discount.creditId || discount.id,
      date: today.toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' }),
      sellerName: discount.sellerName || 'N/A',
      sellerIban: discount.sellerIban || 'N/A',
      buyerName: discount.buyerName || 'N/A',
      receivableAmount: parseFloat(discount.receivableAmount || 0).toFixed(2),
      dueDate: dueDate.toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' }),
      termDays: discount.termDays || 0,
      annualInterestRate: parseFloat(discount.annualInterestRate || 0).toFixed(2),
      commissionRate: parseFloat(discount.commissionRate || 0).toFixed(2),
      transactionFee: parseFloat(discount.transactionFee || 0).toFixed(2),
      commission: parseFloat(discount.commission || 0).toFixed(2),
      netPayment: parseFloat(discount.netPayment || 0).toFixed(2),
      currency,
    };
  };

  // Handle view contract
  const handleViewContract = async (discount) => {
    setSelectedDiscount(discount);
    setLoadingContract(true);
    setContractDialog(true);
    setLoadingContract(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ 
                  width: 64, 
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  border: '3px solid rgba(255,255,255,0.3)',
                  fontSize: '1.5rem'
                }}>
                  {user?.name && user?.surname ? 
                    `${user.name.charAt(0)}${user.surname.charAt(0)}`.toUpperCase() : 
                    user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Discount Loans
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  View and manage your discount loan requests
                </Typography>
              </Grid>
              <Grid item>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Refresh />}
                    onClick={fetchDiscounts}
                    disabled={loading}
                    sx={{
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.3)',
                      }
                    }}
                  >
                    {loading ? 'Loading...' : 'Refresh'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/user/discount/create')}
                    sx={{
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.3)',
                      }
                    }}
                  >
                    New Discount Credit
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>
            
            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Discounts Table */}
            <Card elevation={8} sx={{ borderRadius: 3 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Discount Number</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Receivable Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Due Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.875rem' }}>Net Payment</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center', fontSize: '0.875rem' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center', fontSize: '0.875rem' }}>Contract</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center', fontSize: '0.875rem' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 8 }}>
                        <CircularProgress size={40} sx={{ color: '#1976d2' }} />
                        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontSize: '0.875rem' }}>Loading discounts...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : discounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 8 }}>
                        <Box sx={{ display: 'inline-flex', p: 3, borderRadius: 3, backgroundColor: 'grey.100', mb: 2 }}>
                          <AttachMoney sx={{ fontSize: 48, color: 'grey.400' }} />
                        </Box>
                        <Typography variant="h6" sx={{ mb: 1, color: 'text.primary', fontSize: '1rem' }}>No Discount Loans</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>No discount loans found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    discounts.map((discount) => (
                      <TableRow 
                        key={discount.id}
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            borderLeftColor: discount.status === 1 ? 'success.main' : discount.status === 4 ? 'error.main' : 'grey.300'
                          },
                          borderLeft: '4px solid transparent'
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                          {discount.discountNumber || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                          {formatCurrency(discount.receivableAmount, 'EUR')}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                          {formatDate(discount.dueDate)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: 'success.main', fontSize: '0.8rem' }}>
                          {formatCurrency(discount.netPayment, 'EUR')}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', fontSize: '0.8rem' }}>
                          <Chip
                            label={discount.statusText || getStatusText(discount.status)}
                            size="small"
                            color={getStatusColor(discount.status)}
                            sx={{
                              fontWeight: 600,
                              borderRadius: 3,
                              color: 'white',
                              fontSize: '0.75rem',
                              height: 24
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', fontSize: '0.8rem' }}>
                          <Tooltip title="View Contract">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewContract(discount)}
                              sx={{
                                color: 'primary.main',
                                backgroundColor: 'primary.lighter',
                                '&:hover': {
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <Description fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', fontSize: '0.8rem' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDetails(discount)}
                              sx={{
                                color: 'primary.main',
                                backgroundColor: 'primary.lighter',
                                '&:hover': {
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {discount.status === 1 && (
                            <Tooltip title="Cancel">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleCancel(discount.id)}
                                sx={{
                                  color: 'error.main',
                                  backgroundColor: 'error.lighter',
                                  '&:hover': {
                                    backgroundColor: 'error.main',
                                    color: 'white',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s',
                                  ml: 1
                                }}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            </Card>
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog
          open={detailDialog}
          onClose={() => setDetailDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              maxHeight: '90vh',
              overflow: 'hidden'
            }
          }}
        >
          {selectedDiscount && (
            <>
              {/* Header with gradient background */}
              <Box sx={{ 
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                color: 'white',
                p: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ 
                    width: 56, 
                    height: 56,
                    background: 'rgba(255,255,255,0.25)',
                    border: '3px solid rgba(255,255,255,0.3)'
                  }}>
                    <Business sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {selectedDiscount.sellerName || 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Seller ID: {selectedDiscount.sellerId}
                    </Typography>
                    {selectedDiscount.discountNumber && (
                      <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                        Discount Number: {selectedDiscount.discountNumber}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={selectedDiscount.statusText || getStatusText(selectedDiscount.status)}
                    sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.25)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      border: '1px solid rgba(255,255,255,0.3)',
                      px: 2
                    }}
                  />
                </Box>
                
                {/* Amount highlight */}
                <Paper sx={{ 
                  p: 2.5,
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5, color: 'white' }}>
                    Net Payment Amount
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                    {formatCurrency(selectedDiscount.netPayment, selectedDiscount.currency || 'EUR')}
                  </Typography>
                </Paper>
              </Box>

              <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 3 }}>
                  {/* Timeline and Financial Info */}
                  <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2}>
                      {/* Timeline Section */}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                          Timeline
                        </Typography>
                        <Paper sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                                  Due Date
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, pl: 4 }}>
                                {formatDate(selectedDiscount.dueDate)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <AttachMoney sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                                  Term (Days)
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, pl: 4 }}>
                                {selectedDiscount.termDays} days
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>

                      {/* Financial Details Section */}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                          Financial Details
                        </Typography>
                        <Paper sx={{ p: 2, backgroundColor: 'primary.lighter', borderRadius: 3, border: '2px solid', borderColor: 'primary.main' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Receivable Amount
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.main' }}>
                                {formatCurrency(selectedDiscount.receivableAmount, selectedDiscount.currency || 'EUR')}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Transaction Fee
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                                - {formatCurrency(selectedDiscount.transactionFee, selectedDiscount.currency || 'EUR')}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Commission
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                                - {formatCurrency(selectedDiscount.commission, selectedDiscount.currency || 'EUR')}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Parties Info */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 700, 
                      mb: 2,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      letterSpacing: 1
                    }}>
                      Parties
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ 
                          p: 2.5, 
                          backgroundColor: 'white',
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'grey.200'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Person sx={{ fontSize: 20, color: 'primary.main' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                              Seller
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {selectedDiscount.sellerName || 'N/A'}
                          </Typography>
                          {selectedDiscount.sellerIban && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              IBAN: {selectedDiscount.sellerIban}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ 
                          p: 2.5, 
                          backgroundColor: 'white',
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'grey.200'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Business sx={{ fontSize: 20, color: 'secondary.main' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                              Buyer
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {selectedDiscount.buyerName || 'N/A'}
                          </Typography>
                          {selectedDiscount.buyerIban && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              IBAN: {selectedDiscount.buyerIban}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Additional Info */}
                  {selectedDiscount.contractReference && (
                    <Box sx={{ mb: 2 }}>
                      <Paper sx={{ 
                        p: 2.5, 
                        backgroundColor: 'info.light',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'info.main'
                      }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>
                          Contract Reference
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {selectedDiscount.contractReference}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                  {selectedDiscount.rejectionReason && (
                    <Box sx={{ mb: 2 }}>
                      <Paper sx={{ 
                        p: 2.5, 
                        backgroundColor: 'error.light',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'error.main'
                      }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>
                          Rejection Reason
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.dark' }}>
                          {selectedDiscount.rejectionReason}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Box>
              </DialogContent>

              <DialogActions sx={{ 
                px: 3, 
                py: 2, 
                borderTop: 2, 
                borderColor: 'divider',
                backgroundColor: 'grey.50'
              }}>
                <Button 
                  onClick={() => setDetailDialog(false)} 
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: 3,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem'
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Contract Dialog */}
        <Dialog
          open={contractDialog}
          onClose={() => {
            setContractDialog(false);
            setSelectedDiscount(null);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              py: 2.5,
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              Receivable Assignment Contract
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {loadingContract ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : selectedDiscount ? (
              (() => {
                const contractData = generateContractData(selectedDiscount);
                if (!contractData) {
                  return <Alert severity="warning" sx={{ m: 3 }}>Contract data not found</Alert>;
                }

                return (
                  <Box sx={{ p: 3, maxHeight: '70vh', overflow: 'auto' }}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 4,
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {/* Header */}
                      <Box sx={{ textAlign: 'center', mb: 4, pb: 3, borderBottom: '2px solid', borderColor: 'divider' }}>
                        <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ mb: 1 }}>
                          RECEIVABLE ASSIGNMENT CONTRACT
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Contract No: {contractData.contractId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Date: {contractData.date}
                        </Typography>
                      </Box>

                      {/* Parties */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                          PARTIES
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                1. CREDITOR (SELLER)
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Name/Company:
                              </Typography>
                              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                                {contractData.sellerName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                IBAN:
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {contractData.sellerIban}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                2. ASSIGNEE (BANK)
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                DPG Finance Bank
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                3. DEBTOR (BUYER)
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Name/Company:
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {contractData.buyerName}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Subject */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'primary.main' }}>
                          SUBJECT
                        </Typography>
                        <Typography variant="body2" sx={{ lineHeight: 1.8, color: 'text.primary' }}>
                          With this contract, the Creditor (Seller) assigns the receivable specified below to the Bank in an irrevocable (non-recourse) manner.
                        </Typography>
                      </Box>

                      {/* Receivable Info */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'primary.main' }}>
                          RECEIVABLE INFORMATION
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Receivable Amount
                              </Typography>
                              <Typography variant="h6" fontWeight={600}>
                                {contractData.receivableAmount} {contractData.currency}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Due Date
                              </Typography>
                              <Typography variant="h6" fontWeight={600}>
                                {contractData.dueDate}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Term (Days)
                              </Typography>
                              <Typography variant="h6" fontWeight={600}>
                                {contractData.termDays} days
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Discount Calculations */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'primary.main' }}>
                          DISCOUNT CALCULATIONS
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Annual Interest Rate
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                %{contractData.annualInterestRate}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Commission Rate
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                %{contractData.commissionRate}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Transaction Fee
                              </Typography>
                              <Typography variant="body1" fontWeight={600} color="error.main">
                                - {contractData.transactionFee} {contractData.currency}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Commission
                              </Typography>
                              <Typography variant="body1" fontWeight={600} color="error.main">
                                - {contractData.commission} {contractData.currency}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 1 }}>
                              <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9 }}>
                                Net Payment
                              </Typography>
                              <Typography variant="h6" fontWeight={700}>
                                {contractData.netPayment} {contractData.currency}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Obligations */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'primary.main' }}>
                          OBLIGATIONS
                        </Typography>
                        <Box component="ol" sx={{ pl: 3, '& li': { mb: 1.5, lineHeight: 1.8 } }}>
                          <Typography component="li" variant="body2">
                            The Creditor (Seller) assigns this receivable to the Bank in an irrevocable (non-recourse) manner.
                          </Typography>
                          <Typography component="li" variant="body2">
                            The Creditor (Seller) cannot claim any rights related to this receivable and waives all rights to it.
                          </Typography>
                          <Typography component="li" variant="body2">
                            The Bank has taken over the receivable and will pay the Creditor the net payment amount ({contractData.netPayment} {contractData.currency}).
                          </Typography>
                          <Typography component="li" variant="body2">
                            The Debtor (Buyer) will make payment to the Bank on the due date ({contractData.dueDate}).
                          </Typography>
                          <Typography component="li" variant="body2">
                            By accepting this contract, the Creditor (Seller) is deemed to have fully assigned the receivable and has no right of return.
                          </Typography>
                          <Typography component="li" variant="body2">
                            This contract has been approved electronically and has legal validity.
                          </Typography>
                        </Box>
                      </Box>

                      {/* Signatures */}
                      <Box sx={{ mt: 4, pt: 3, borderTop: '2px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                          SIGNATURES
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                Creditor (Seller)
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Name:</strong> {contractData.sellerName}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>IBAN:</strong> {contractData.sellerIban}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Date:</strong> {contractData.date}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1, color: 'success.main', fontWeight: 600 }}>
                                ✓ Electronic Approval
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                Bank
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Company:</strong> DPG Finance Bank
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Date:</strong> _______________
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Signature:</strong> _______________
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Paper>
                  </Box>
                );
              })()
            ) : (
              <Alert severity="warning" sx={{ m: 3 }}>Contract text not found</Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 0 }}>
            <Button
              onClick={() => {
                setContractDialog(false);
                setSelectedDiscount(null);
              }}
              variant="contained"
              color="primary"
              sx={{ borderRadius: 3 }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default DiscountList;

